/**
 * Chat is the one surface where a moderation hold is an HTTP error rather than
 * a field on a success body.
 *
 * `ConversationService.SendMessage` / `UpdateMessage` persist the row for the
 * admin audit trail and then throw `422 Unprocessable Entity`. The transport
 * therefore fails even though nothing went wrong technically, so this must be
 * told apart from a real send failure before the UI picks its copy: a hold
 * needs guideline guidance, a network drop needs a retry.
 *
 * Detection lives here rather than in a screen so both platforms, and the
 * send and edit paths, agree on what a hold looks like.
 */
import { HTTP_STATUS } from '../../constants';
import type { ApiError, ApiErrorResponseBody } from '../../types';

/** A send/edit rejected because the text breaches the community guidelines. */
export interface ModerationHoldError extends ApiError {
  isModerationHold: true;
}

const readBody = (error: ApiError): ApiErrorResponseBody | undefined => {
  const data = error.response?.data;
  return data && typeof data === 'object' ? data : undefined;
};

/**
 * True for a moderation hold on a conversation mutation.
 *
 * Both the transport status and the body's `statusCode` are checked because
 * this API sometimes declares the status it meant inside the payload.
 */
export const isModerationHoldError = (
  error: unknown,
): error is ModerationHoldError => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  if ((error as ModerationHoldError).isModerationHold === true) {
    return true;
  }
  const apiError = error as ApiError;
  if (apiError.response?.status === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
    return true;
  }
  return readBody(apiError)?.statusCode === HTTP_STATUS.UNPROCESSABLE_ENTITY;
};
